// Záhlaví s lištou příkazů pro vlastní okna aplikací (Kalendář, Soubory, Pošta…) – stejné ovládání jako seznamy
// a záznamy jádra (koncepce A, vzor Kontakty – podklady/vzor-kontakty-kontrolni-seznam.md):
//  • PC: ☰ · název · lišta · [vlastní prvky] · hledání (úzké okno: lupa) · ⚙
//  • telefon: ☰ · název · 🔍 · [tlačítka] · ⋮ a plovoucí tlačítko hlavní akce; hledání se otevře přes záhlaví (← zavře),
//    režim výběru ✕ · Vybráno N · vybrat vše a hromadné akce dole; stránka záznamu ← · název · hlavní akce · ⋮
// Osobní volba „klasické ovládání“ (Přizpůsobit) vrací pás karet – rozhodne useBar().
import { h, debounce } from '../dom';
import { t } from '../i18n';
import { icon } from '../icons';
import { IwMenu, type MenuItem } from './menu';
import { IwCommandBar, barMenuItem, headButton, type BarItem } from './commandbar';
import type { IwWindow } from '../wm/window';
import type { WindowManager } from '../wm/manager';

/** Nové ovládání (lišta v záhlaví), pokud si uživatel nezvolil klasický pás karet. */
export function useBar(): boolean {
  const prefs = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
  return prefs['ui.chrome'] !== 'ribbon';
}

/** ⋮ na telefonu: všechny akce kromě hlavní (ta je plovoucí), méně časté, zobrazení, Minimalizovat a Zavřít okno. */
export function kebabItems(bar: IwCommandBar, win: IwWindow, withPrimary = false): MenuItem[] {
  const all = bar.all();
  const out: MenuItem[] = all.filter((b) => (b.place === 'action' || b.place === 'selection' || (withPrimary && b.place === 'primary')) && !b.disabled).map(barMenuItem);
  const more = all.filter((b) => b.place === 'more' && !b.disabled).map(barMenuItem);
  const view = all.filter((b) => b.place === 'view' && !b.disabled).map(barMenuItem);
  if (more.length) out.push(...(out.length ? [{ separator: true, label: '' }] : []), ...more);
  if (view.length) out.push(...(out.length ? [{ separator: true, label: '' }] : []), { header: true, label: t('core.ui.view_settings') }, ...view);
  const ex = bar.extraItems();
  if (ex?.items.length) out.push(...(out.length ? [{ separator: true, label: '' }] : []), { header: true, label: ex.label }, ...ex.items);
  out.push({ separator: true, label: '' }, { label: t('core.ui.minimize'), icon: 'minimize', onSelect: () => win.minimize() }, { label: t('core.ui.close_window'), icon: 'close', onSelect: () => void win.close() });
  return out;
}

/**
 * Hledání v záhlaví okna – stejné jako u seznamů jádra: na PC pole vpravo v záhlaví (úzké okno: lupa), na telefonu 🔍,
 * které otevře pole přes celé záhlaví; ← nebo krok zpět ho zavře a hledání vymaže. onChange dostane text (debounce).
 */
export class HeadSearch {
  readonly input: HTMLInputElement;
  readonly wrap: HTMLElement;
  open = false;
  private back = 0;
  /** překreslit záhlaví (nastaví renderBarHeader) */
  rerender: () => void = () => undefined;
  private wm: WindowManager | null = null;
  private win: IwWindow | null = null;

  constructor(private onChange: (q: string) => void, placeholder?: string) {
    this.input = h('input', { class: 'input search', type: 'search', placeholder: placeholder ?? t('core.ui.search_placeholder'), 'aria-label': t('core.cmd.search') }) as HTMLInputElement;
    this.wrap = h('div', { class: 'search-wrap head-search' }, icon('search'), this.input);
    const fire = debounce(() => this.onChange(this.input.value.trim()), 350);
    this.input.addEventListener('input', () => fire());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        fire.cancel();
        this.onChange(this.input.value.trim());
      }
    });
  }

  value(): string {
    return this.input.value.trim();
  }

  /** Nastavit text zvenku (např. hledání z jiného místa okna) – bez onChange. */
  set(q: string): void {
    this.input.value = q;
  }

  attach(wm: WindowManager, win: IwWindow): void {
    this.wm = wm;
    this.win = win;
  }

  /** Telefon: otevřít / zavřít hledání v záhlaví (krok zpět ho zavře; zavření hledání vymaže). */
  toggle(open: boolean): void {
    const wm = this.wm;
    if (wm && open && !wm.hasBack(this.back)) this.back = wm.pushBack(() => { this.back = 0; this.toggle(false); return true; }, this.win);
    if (wm && !open && this.back) {
      const id = this.back;
      this.back = 0;
      wm.dropBack(id);
    }
    this.open = open;
    if (!open && this.input.value) {
      this.input.value = '';
      this.onChange('');
    }
    this.rerender();
    if (open) this.input.focus();
  }

  /** Ctrl+F: na telefonu nejdřív otevřít, pak pole vybrat. */
  focus(): void {
    if (this.wm?.currentMode() === 'mobile' && !this.open) this.toggle(true);
    this.win?.classList.add('pc-search');
    this.input.focus();
    this.input.select();
  }
}

export interface BarHeader {
  win: IwWindow;
  wm: WindowManager;
  bar: IwCommandBar;
  /** ☰ – navigační panel okna (složky, kalendáře) */
  menu?: { label: string; onClick: () => void } | null;
  /** stránka záznamu / zprávy: na telefonu ← vlevo (zavře okno) a hlavní akce jako ikona v záhlaví (bez plovoucího tlačítka) */
  back?: boolean;
  /** PC: prvky za lištou */
  after?: Node[];
  /** telefon: tlačítka v záhlaví před ⋮ (Dnes…) */
  mobile?: Node[];
  /** hledání v záhlaví (PC pole / telefon 🔍) */
  search?: HeadSearch | null;
  /** telefon, režim výběru: ✕ · Vybráno N · vybrat vše místo záhlaví */
  select?: { count: number; onCancel: () => void; onAll: () => void } | null;
  /** plovoucí tlačítko hlavní akce (telefon) – naplní se z lišty */
  fab?: HTMLButtonElement | null;
}

/** Postaví záhlaví okna podle režimu (volat po změně položek lišty, výběru a po změně režimu PC / telefon). */
export function renderBarHeader(o: BarHeader): void {
  const mobile = o.wm.currentMode() === 'mobile';
  const search = o.search ?? null;
  if (search) {
    search.attach(o.wm, o.win);
    search.rerender = () => renderBarHeader(o);
    if (!mobile) search.open = false;
  }
  const selecting = mobile && Boolean(o.select);
  const searching = mobile && !selecting && Boolean(search?.open);
  o.win.classList.toggle('sel-mode', selecting);
  o.win.classList.toggle('search-open', searching);
  const start: Node[] = [];
  const main: Node[] = [];
  const primary = o.bar.all().find((b) => b.place === 'primary' && !b.disabled);
  if (selecting && o.select) {
    start.push(headButton('x', t('core.ui.cancel'), () => o.select?.onCancel()));
    main.push(h('span', { class: 'head-sel-count' }, t('core.ui.selected', { count: o.select.count })), headButton('check-square', t('core.cmd.select_all'), () => o.select?.onAll()));
  } else if (searching && search) {
    start.push(headButton('arrow-left', t('core.ui.close'), () => search.toggle(false)));
    main.push(search.wrap);
  } else if (mobile) {
    if (o.back) start.push(headButton('arrow-left', t('core.ui.back'), () => void o.win.close()));
    if (o.menu) start.push(headButton('menu', o.menu.label, () => o.menu?.onClick(), 'head-menu'));
    main.push(h('span', { class: 'head-spacer' }));
    if (search) main.push(headButton('search', t('core.cmd.search'), () => search.toggle(true)));
    main.push(...(o.mobile ?? []));
    if (o.back && primary) main.push(headButton(primary.icon ?? 'check', primary.label, (b) => (primary.menu ? IwMenu.showAt(primary.menu, b) : primary.onClick?.()), 'head-primary'));
    main.push(headButton('more-vertical', t('core.ui.menu'), (b) => IwMenu.showAt(kebabItems(o.bar, o.win, false), b)));
  } else {
    if (o.menu) start.push(headButton('menu', o.menu.label, () => o.menu?.onClick(), 'head-menu'));
    main.push(o.bar, ...(o.after ?? []));
    if (search) {
      // úzké okno (< 640 px): hledání se rozbalí z lupy přes lištu (stejně jako u seznamů)
      main.push(search.wrap, headButton('search', t('core.cmd.search'), () => { o.win.classList.add('pc-search'); search.input.focus(); }, 'head-search-btn'));
      search.input.onblur = () => { if (!search.input.value) o.win.classList.remove('pc-search'); };
    }
    const view = o.bar.all().filter((b) => b.place === 'view');
    if (view.length) main.push(headButton('settings', t('core.ui.view_settings'), (b) => IwMenu.showAt(view.map(barMenuItem), b), 'head-gear'));
  }
  o.win.setHeader({ start, main });
  if (o.fab) {
    o.fab.hidden = !mobile || !primary || Boolean(o.back) || selecting || Boolean(primary?.disabled);
    if (primary) {
      const fab = o.fab;
      fab.replaceChildren(icon(primary.icon ?? 'plus'), h('span', null, primary.label));
      fab.onclick = () => (primary.menu ? IwMenu.showAt(primary.menu, fab) : primary.onClick?.());
    }
  }
}

/**
 * Lišta v záhlaví pro jednoduchá vlastní okna (O systému, Import…, Asistent): hlavní akce a pár dalších místo tlačítek
 * v obsahu. Vrátí funkci, která lištu překreslí (po změně stavu – běží import…). Klasické ovládání: nic nedělá
 * (okno má dál svá tlačítka v obsahu – vrátí false v `active`).
 */
export function simpleBar(win: IwWindow, wm: WindowManager, items: () => BarItem[]): { refresh: () => void; active: boolean } {
  if (!useBar()) return { refresh: () => undefined, active: false };
  const bar = new IwCommandBar();
  const refresh = () => {
    bar.setItems(items());
    renderBarHeader({ win, wm, bar });
  };
  refresh();
  return { refresh, active: true };
}

/** Plovoucí tlačítko (telefon); pohled ho vloží do svého prvku s třídou chrome-bar. */
export function fabButton(): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'fab' }) as HTMLButtonElement;
  b.hidden = true;
  return b;
}

/** Telefon v režimu výběru: hromadné akce dole v dosahu palce (nejvýš 4, zbytek pod Další) – stejně jako seznamy jádra. */
export function renderSelBar(el: HTMLElement, items: BarItem[], on: boolean): void {
  el.classList.add('sel-bar');
  el.hidden = !on;
  if (!on) return;
  const acts = items.filter((b) => b.place === 'selection' && !b.disabled);
  const shown = acts.slice(0, acts.length > 4 ? 3 : 4);
  const rest = acts.slice(shown.length);
  const btn = (label: string, ic: string, fn: (b: HTMLButtonElement) => void, danger = false): HTMLButtonElement => {
    const b = h('button', { type: 'button', class: 'sel-act' + (danger ? ' danger' : '') }, icon(ic), h('span', null, label)) as HTMLButtonElement;
    b.addEventListener('click', () => fn(b));
    return b;
  };
  el.replaceChildren(...shown.map((b) => btn(b.label, b.icon ?? 'check', (x) => { if (b.menu) IwMenu.showAt(b.menu, x); else b.onClick?.(); }, b.danger)), ...(rest.length ? [btn(t('core.ui.more'), 'more', (x) => IwMenu.showAt(rest.map(barMenuItem), x))] : []));
}

/** Štítek filtru nad seznamem (vzor Kontakty): popisek s ▾, aktivní zvýrazněný; klepnutí otevře výběr. */
export function filterChip(id: string, label: string, active: boolean, onClick: (el: HTMLButtonElement) => void, colorDot?: string | null): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'fchip' + (active ? ' active' : ''), dataset: { id } }, colorDot ? h('span', { class: 'layer-dot', style: { background: colorDot } }) : null, h('span', { class: 'fchip-label' }, label), icon('chevron-down', 'icon fchip-caret')) as HTMLButtonElement;
  b.addEventListener('click', () => onClick(b));
  return b;
}

/** Štítek „Zrušit filtry“ (jen když je nějaký filtr zapnutý). */
export function clearFiltersChip(onClick: () => void): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'fchip clear', title: t('core.ui.clear_filters') }, icon('x'), h('span', { class: 'fchip-label' }, t('core.ui.clear_filters'))) as HTMLButtonElement;
  b.addEventListener('click', onClick);
  return b;
}

export type { BarItem };
